import { unzipSync } from "fflate"

export type ServiceLevelDetected = {
  unknown?: false
  filename: string
  accountCode: string
  accountName: string
  reportType: string
  templateVersion: string
  periodYear: number
  periodMonth: number
  rowCount: number
}

export type ServiceLevelUnknown = {
  unknown: true
  filename: string
  reason: string
}

export type ServiceLevelResult = ServiceLevelDetected | ServiceLevelUnknown

const ACCOUNT_NAMES: Record<string, string> = {
  IDG: "Indogrosir",
  IDM: "Indomaret",
  MIDI: "PT MIDI UTAMA INDONESIA Tbk",
  SAT: "PT SUMBER ALFARIA TRIJAYA Tbk",
  SIL: "PT SUMBER INDAH LESTARI",
}

const MONTH_MAP: Record<string, number> = {
  jan: 1, january: 1, januari: 1,
  feb: 2, february: 2, februari: 2,
  mar: 3, march: 3, maret: 3,
  apr: 4, april: 4,
  may: 5, mei: 5,
  jun: 6, june: 6, juni: 6,
  jul: 7, july: 7, juli: 7,
  aug: 8, august: 8, agustus: 8,
  sep: 9, september: 9,
  oct: 10, october: 10, oktober: 10,
  nov: 11, november: 11,
  dec: 12, december: 12, desember: 12,
}

export async function parseServiceLevelForPreview(file: File): Promise<ServiceLevelResult> {
  const filename = file.name

  try {
    const buf = await file.arrayBuffer()
    const data = new Uint8Array(buf)
    const decode = (b: Uint8Array) => new TextDecoder().decode(b)

    // Step 1: extract workbook metadata
    const meta = unzipSync(data, {
      filter: (f) => f.name === "xl/workbook.xml" || f.name === "xl/_rels/workbook.xml.rels",
    })
    if (!meta["xl/workbook.xml"]) {
      return { unknown: true, filename, reason: "workbook.xml tidak ditemukan dalam file." }
    }

    const wbXml = decode(meta["xl/workbook.xml"])
    const sheetEntries = parseSheetEntries(wbXml)
    const sheetNames = sheetEntries.map((e) => e.name)
    const relsXml = meta["xl/_rels/workbook.xml.rels"] ? decode(meta["xl/_rels/workbook.xml.rels"]) : ""

    // Step 2: detect template
    if (sheetNames.includes("SLProdMon")) {
      return detectIdg(data, decode, filename, sheetEntries, relsXml)
    }

    if (sheetNames.includes("REG") || sheetNames.includes("SC")) {
      return detectIdm(data, decode, filename, sheetNames, sheetEntries, relsXml)
    }

    if (sheetNames.includes("Worksheet")) {
      return detectWorksheet(data, decode, filename, sheetEntries, relsXml)
    }

    if (sheetNames.includes("Sheet1")) {
      return detectSat2023Tall(data, decode, filename, sheetEntries, relsXml)
    }

    return {
      unknown: true,
      filename,
      reason: `Template tidak dikenal. Sheet ditemukan: ${sheetNames.join(", ") || "(tidak ada)"}`,
    }
  } catch (err) {
    return {
      unknown: true,
      filename,
      reason: err instanceof Error ? err.message : "Gagal membaca file",
    }
  }
}

// ---------------------------------------------------------------------------
// IDG detection
// ---------------------------------------------------------------------------

function detectIdg(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): ServiceLevelResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "SLProdMon")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet SLProdMon tidak ditemukan." }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row3 = getRowXml(rowParts, 3)
  if (!row3) {
    return { unknown: true, filename, reason: "File Indogrosir tidak lengkap (Baris 3 kosong)." }
  }

  const row3Val = rowCellValues(row3, ss).compact().map((v) => v.toString()).join(" ")
  const match = row3Val.match(/PRODUCT MONTHLY\s*\(\s*([A-Za-z]+)\s+(\d{4})\s*\)/i)
  if (!match) {
    return { unknown: true, filename, reason: `Format judul baris 3 file Indogrosir tidak sesuai: "${row3Val}".` }
  }

  const monthStr = match[1]
  const year = parseInt(match[2], 10)
  const month = MONTH_MAP[monthStr.toLowerCase()]
  if (!month) {
    return { unknown: true, filename, reason: `Bulan "${monthStr}" tidak dikenal pada baris 3 file Indogrosir.` }
  }

  const reportType = filename.toLowerCase().includes("safi") ? "safi" : "reguler"

  // Count active rows
  const rowCount = countIdgRows(rowParts, ss)

  return {
    filename,
    accountCode: "IDG",
    accountName: ACCOUNT_NAMES["IDG"],
    reportType,
    templateVersion: "idg_v1",
    periodYear: year,
    periodMonth: month,
    rowCount,
  }
}

function countIdgRows(rowParts: string[], ss: string[]): number {
  const row6 = getRowXml(rowParts, 6)
  const row7 = getRowXml(rowParts, 7)
  if (!row6 || !row7) return 0

  const dcMap: Record<string, string> = {}
  const row6Cells = row6.split("<c ").slice(1)
  row6Cells.eachCell((cellXml) => {
    const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
    if (!colLetter) return
    const dcName = xlsxCellString("<c " + cellXml, ss)?.trim()
    if (!dcName) return

    const colIdx = columnLetterToIndex(colLetter)
    const subHeaders = [0, 2, 4].map((offset) => {
      const targetCol = columnIndexToLetter(colIdx + offset)
      return cellValueForColumn(row7, targetCol, ss)?.toString().trim().toUpperCase() ?? ""
    })

    if (subHeaders[0] === "QTY PO" && subHeaders[1] === "QTY BPB" && subHeaders[2] === "S/L") {
      dcMap[dcName] = colLetter
    }
  })

  let count = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < 8) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const itemStr = cellValueForColumn(rowXml, "B", ss)?.toString().trim()
    if (!itemStr) continue

    // For each DC, see if PO Qty or Receipt Qty is non-zero
    Object.values(dcMap).forEach((startCol) => {
      const colIdx = columnLetterToIndex(startCol)
      const poQty = numericCellValue(rowXml, columnIndexToLetter(colIdx))
      const poVal = numericCellValue(rowXml, columnIndexToLetter(colIdx + 1))
      const recQty = numericCellValue(rowXml, columnIndexToLetter(colIdx + 2))
      const recVal = numericCellValue(rowXml, columnIndexToLetter(colIdx + 3))

      if ((poQty && poQty !== 0) || (poVal && poVal !== 0) || (recQty && recQty !== 0) || (recVal && recVal !== 0)) {
        count++
      }
    })
  }

  return count
}

// ---------------------------------------------------------------------------
// IDM detection
// ---------------------------------------------------------------------------

function detectIdm(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetNames: string[],
  sheetEntries: SheetEntry[],
  relsXml: string
): ServiceLevelResult {
  const sheetName = sheetNames.includes("SC") ? "SC" : "REG"
  const wsPath = resolveSheetPath(sheetEntries, relsXml, sheetName)
  if (!wsPath) return { unknown: true, filename, reason: `Sheet ${sheetName} tidak ditemukan.` }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row2 = getRowXml(rowParts, 2)
  if (!row2) {
    return { unknown: true, filename, reason: "File Indomaret tidak lengkap." }
  }

  const cellA2Val = cellValueForColumn(row2, "A", ss)?.toString().trim() ?? ""
  const match = cellA2Val.match(/^(\d{4})-(\d{2})$/)
  if (!match) {
    return { unknown: true, filename, reason: `Format periode di sel A2 Indomaret tidak sesuai YYYY-MM: "${cellA2Val}".` }
  }

  const year = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const reportType = sheetName === "SC" ? "safi" : "reguler"

  // Count active rows where PRODUCT is non-empty
  const row1 = getRowXml(rowParts, 1)
  let productCol = "E"
  if (row1) {
    for (const cellXml of row1.split("<c ").slice(1)) {
      const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
      if (!colLetter) continue
      const name = xlsxCellString("<c " + cellXml, ss)?.trim().toUpperCase()
      if (name === "PRODUCT") {
        productCol = colLetter
        break
      }
    }
  }

  let count = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < 2) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const productStr = cellValueForColumn(rowXml, productCol, ss)?.toString().trim()
    if (productStr) {
      // check if it has non-empty PO / receipt values to filter out inactive rows
      count++
    }
  }

  return {
    filename,
    accountCode: "IDM",
    accountName: ACCOUNT_NAMES["IDM"],
    reportType,
    templateVersion: "idm_v1",
    periodYear: year,
    periodMonth: month,
    rowCount: count,
  }
}

// ---------------------------------------------------------------------------
// Worksheet (Wide merged layout) detection
// ---------------------------------------------------------------------------

function detectWorksheet(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): ServiceLevelResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "Worksheet")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet Worksheet tidak ditemukan." }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row1 = getRowXml(rowParts, 1)
  if (!row1) return { unknown: true, filename, reason: "Sheet Worksheet tidak lengkap (Baris 1 kosong)." }

  const row1Val = rowCellValues(row1, ss).compact().map((v) => v.toString()).join(" ").trim()
  const row3 = getRowXml(rowParts, 3)
  const row3Val = row3 ? rowCellValues(row3, ss).compact().map((v) => v.toString()).join(" ").trim() : ""

  let accountCode: string | null = null
  if (row1Val.startsWith("PT SUMBER ALFARIA TRIJAYA")) {
    accountCode = "SAT"
  } else if (row1Val.startsWith("PT SUMBER INDAH LESTARI")) {
    accountCode = "SIL"
  } else if (row1Val.startsWith("PT MIDI UTAMA INDONESIA") || row3Val.startsWith("PT MIDI UTAMA INDONESIA")) {
    accountCode = "MIDI"
  } else {
    return { unknown: true, filename, reason: `Judul perusahaan tidak dikenal: "${row1Val}".` }
  }

  const row4 = getRowXml(rowParts, 4)
  if (!row4) return { unknown: true, filename, reason: "Sheet Worksheet tidak lengkap (Baris 4 kosong)." }

  const periodStr = cellValueForColumn(row4, "A", ss)?.toString().trim() ?? ""
  const match = periodStr.match(/^([A-Za-z]{3})-(\d{2})$/)
  if (!match) {
    return { unknown: true, filename, reason: `Format periode di sel A4 tidak sesuai MMM-YY: "${periodStr}".` }
  }

  const monthStr = match[1]
  const yearShort = parseInt(match[2], 10)
  const year = 2000 + yearShort
  const month = MONTH_MAP[monthStr.toLowerCase()]
  if (!month) {
    return { unknown: true, filename, reason: `Bulan "${monthStr}" tidak dikenal pada sel A4.` }
  }

  const reportType = filename.toLowerCase().includes("safi") ? "safi" : "reguler"

  // Count active rows
  const rowCount = countWorksheetRows(rowParts, ss)

  return {
    filename,
    accountCode,
    accountName: ACCOUNT_NAMES[accountCode],
    reportType,
    templateVersion: "wide_v1",
    periodYear: year,
    periodMonth: month,
    rowCount,
  }
}

function countWorksheetRows(rowParts: string[], ss: string[]): number {
  const row5 = getRowXml(rowParts, 5)
  const row6 = getRowXml(rowParts, 6)
  if (!row5 || !row6) return 0

  const dcMap: Record<string, string> = {}
  const row5Cells = row5.split("<c ").slice(1)
  row5Cells.eachCell((cellXml) => {
    const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
    if (!colLetter) return
    const dcName = xlsxCellString("<c " + cellXml, ss)?.trim()
    if (!dcName || dcName.toUpperCase() === "NATIONAL") return

    const colIdx = columnLetterToIndex(colLetter)
    const subHeaders = [0, 1, 2].map((offset) => {
      const targetCol = columnIndexToLetter(colIdx + offset)
      return cellValueForColumn(row6, targetCol, ss)?.toString().trim().toUpperCase() ?? ""
    })

    if (subHeaders[0] === "PO" && subHeaders[1] === "REAL" && subHeaders[2] === "%") {
      dcMap[dcName] = colLetter
    }
  })

  let count = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < 7) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const idxVal = numericCellValue(rowXml, "A")
    if (!idxVal || idxVal <= 0) continue

    const plu = cellValueForColumn(rowXml, "B", ss)?.toString().trim()
    if (!plu) continue

    Object.values(dcMap).forEach((startCol) => {
      const colIdx = columnLetterToIndex(startCol)
      // Values may be stored as shared strings (t="s") in some templates (SIL, MIDI),
      // so we use cellValueForColumn which handles all cell types, then parse as number.
      const poRaw = cellValueForColumn(rowXml, columnIndexToLetter(colIdx), ss)
      const recRaw = cellValueForColumn(rowXml, columnIndexToLetter(colIdx + 1), ss)

      const poVal = parseNumericString(poRaw)
      const recVal = parseNumericString(recRaw)

      if ((poVal !== null && poVal !== 0) || (recVal !== null && recVal !== 0)) {
        count++
      }
    })
  }

  return count
}

/** Parse a value that might be a number or a formatted number string (e.g. "3,843,994") */
function parseNumericString(val: string | number | null): number | null {
  if (val === null || val === undefined) return null
  if (typeof val === "number") return val
  const cleaned = val.toString().replace(/[,\s]/g, "")
  if (cleaned === "") return null
  const num = parseFloat(cleaned)
  return isNaN(num) ? null : num
}


// ---------------------------------------------------------------------------
// Sheet1 (SAT 2023 Tall Layout) detection
// ---------------------------------------------------------------------------

function detectSat2023Tall(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): ServiceLevelResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "Sheet1")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet Sheet1 tidak ditemukan." }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row2 = getRowXml(rowParts, 2)
  if (!row2) {
    return { unknown: true, filename, reason: "File Sheet1 tidak lengkap (Baris 2 kosong)." }
  }

  const headers = rowCellValues(row2, ss).compact().map((v) => v.toString().trim().toUpperCase())
  if (!headers.includes("MONTH") || !headers.includes("YEAR") || !headers.includes("DC") || !headers.includes("PLU")) {
    return { unknown: true, filename, reason: "Header Sheet1 tidak sesuai untuk format SAT 2023 Tall." }
  }

  const row3 = getRowXml(rowParts, 3)
  if (!row3) {
    return { unknown: true, filename, reason: "File Sheet1 tidak memiliki data." }
  }

  const monthStr = cellValueForColumn(row3, "B", ss)?.toString().trim()
  const yearVal = cellValueForColumn(row3, "C", ss)?.toString().trim()

  const month = MONTH_MAP[monthStr?.toLowerCase() ?? ""]
  const year = yearVal ? parseInt(yearVal, 10) : 0

  if (!year || !month) {
    return { unknown: true, filename, reason: `Tidak bisa membaca periode dari data baris pertama SAT 2023 Tall (MONTH="${monthStr}", YEAR="${yearVal}").` }
  }

  const reportType = filename.toLowerCase().includes("safi") ? "safi" : "reguler"

  // Count active rows (where PLU is not empty)
  let count = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < 3) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const plu = cellValueForColumn(rowXml, "E", ss)?.toString().trim()
    if (plu) {
      count++
    }
  }

  return {
    filename,
    accountCode: "SAT",
    accountName: ACCOUNT_NAMES["SAT"],
    reportType,
    templateVersion: "sat_2023_v1",
    periodYear: year,
    periodMonth: month,
    rowCount: count,
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SheetEntry = { name: string; rId: string }

function parseSheetEntries(wbXml: string): SheetEntry[] {
  const entries: SheetEntry[] = []
  const re = /<sheet\b[^>]*name="([^"]*)"[^>]*r:id="([^"]*)"[^>]*>/g
  let m
  while ((m = re.exec(wbXml)) !== null) {
    entries.push({ name: m[1], rId: m[2] })
  }
  return entries
}

function resolveSheetPath(
  entries: SheetEntry[],
  relsXml: string,
  sheetName: string
): string | null {
  const entry = entries.find((e) => e.name === sheetName)
  if (!entry) return null
  const relParts = relsXml.split(/<Relationship\b/)
  for (let i = 1; i < relParts.length; i++) {
    if (relParts[i].includes(`Id="${entry.rId}"`)) {
      const targetMatch = relParts[i].match(/\bTarget="([^"]+)"/)
      if (targetMatch) {
        const target = targetMatch[1]
        if (target.startsWith("../")) return target.slice(3)
        if (target.startsWith("/")) return target.slice(1)
        return `xl/${target}`
      }
    }
  }
  return null
}

function parseSharedStrings(bytes: Uint8Array | undefined, decode: (b: Uint8Array) => string): string[] {
  if (!bytes) return []
  const xml = decode(bytes)
  const ss: string[] = []
  for (const part of xml.split("<si>").slice(1)) {
    const matches = [...part.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    ss.push(matches.map((m) => m[1]).join(""))
  }
  return ss
}

function getRowXml(rowParts: string[], rowNumber: number): string {
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    if (attr && parseInt(attr[1], 10) === rowNumber) {
      const end = rowParts[i].indexOf("</row>")
      return end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    }
  }
  return ""
}

function findCellXml(rowXml: string, colLetter: string): string {
  for (const part of rowXml.split("<c ").slice(1)) {
    if (new RegExp(`r="${colLetter}\\d+"`).test(part)) {
      return "<c " + part
    }
  }
  return ""
}

function xlsxCellString(cellXml: string, ss: string[]): string | null {
  if (!cellXml) return null
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? matches.map((m) => m[1]).join("") : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? m[1] : null
  }
  return null
}

function cellValueForColumn(rowXml: string, colLetter: string, ss: string[]): string | number | null {
  const cellXml = findCellXml(rowXml, colLetter)
  if (!cellXml) return null

  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? unescapeXml(matches.map((m) => m[1]).join("")) : null
  }
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? unescapeXml(m[1]) : null
  }
  const m = cellXml.match(/<v>([^<]*)<\/v>/)
  if (m) {
    const rawVal = unescapeXml(m[1])
    const num = parseFloat(rawVal)
    return isNaN(num) ? rawVal : num
  }
  return null
}

function numericCellValue(rowXml: string, colLetter: string): number | null {
  const cellXml = findCellXml(rowXml, colLetter)
  if (!cellXml) return null
  if (cellXml.includes('t="s"') || cellXml.includes('t="str"') || cellXml.includes('t="inlineStr"')) return null
  const m = cellXml.match(/<v>([^<]*)<\/v>/)
  if (!m) return null
  const val = parseFloat(m[1].replace(/[,\s]/g, ""))
  return isNaN(val) ? null : val
}

function rowCellValues(rowXml: string, ss: string[]): (string | number | null)[] {
  const cells = rowXml.split("<c ").slice(1)
  return cells.map((cellXml) => {
    const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
    return colLetter ? cellValueForColumn(rowXml, colLetter, ss) : null
  })
}

function unescapeXml(str: string): string {
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&")
}

function columnLetterToIndex(col: string): number {
  let sum = 0
  for (let i = 0; i < col.length; i++) {
    sum = sum * 26 + (col.charCodeAt(i) - "A".charCodeAt(0) + 1)
  }
  return sum
}

function columnIndexToLetter(colIndex: number): string {
  let temp = colIndex
  let letter = ""
  while (temp > 0) {
    const modulo = (temp - 1) % 26
    letter = String.fromCharCode("A".charCodeAt(0) + modulo) + letter
    temp = Math.floor((temp - modulo) / 26)
  }
  return letter
}

// Extension methods for standard arrays in JS
declare global {
  interface Array<T> {
    compact(): Exclude<T, null | undefined>[]
    eachCell(callback: (value: T, index: number, array: T[]) => void): void
  }
}

if (!Array.prototype.compact) {
  Array.prototype.compact = function () {
    return this.filter((v) => v !== null && v !== undefined)
  }
}

if (!Array.prototype.eachCell) {
  Array.prototype.eachCell = function (callback) {
    this.forEach(callback)
  }
}
